/**
 * Small pure helpers shared across the event-page-revamp components.
 *
 * Keeping these out of the React tree makes them trivially testable and keeps
 * each component focused on rendering.
 */
import dayjs from "dayjs";
import { EarningsFinancials, EventListRow, FinancialMetricBlock, MixedEventStat, QuarterlyStatRow, QuarterlyStatistics, StatWindow } from "@/models/event-page";

/** Format an epoch-ms timestamp in the user's LOCAL timezone, e.g. "Apr 29, 2026". */
export const formatEventDate = (epochMs: number | null | undefined): string => (epochMs ? dayjs(epochMs).format("MMM DD, YYYY") : "—");

/**
 * Format the event time in the user's LOCAL timezone with its short timezone
 * abbreviation, e.g. "07:00 EDT". Uses native Intl (no dayjs timezone plugin).
 * Renders client-side only, so there is no SSR/hydration mismatch risk.
 */
export const formatEventTimeLocal = (epochMs: number | null | undefined): string => {
    if (!epochMs) return "";
    const d = new Date(epochMs);
    const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
    const tz = new Intl.DateTimeFormat([], { timeZoneName: "short" }).formatToParts(d).find((p) => p.type === "timeZoneName")?.value ?? "";
    return tz ? `${time} ${tz}` : time;
};

/** Human "43 days ago" / "in 48 days" relative label for an epoch-ms date. */
export const relativeFromNow = (epochMs: number | null | undefined): string => {
    if (!epochMs) return "";
    const target = dayjs(epochMs).startOf("day");
    const today = dayjs().startOf("day");
    const diff = target.diff(today, "day");
    if (diff === 0) return "today";
    if (diff > 0) return `in ${diff} day${diff === 1 ? "" : "s"}`;
    const abs = Math.abs(diff);
    return `${abs} day${abs === 1 ? "" : "s"} ago`;
};

export type EventStatus = "reported" | "confirmed" | "estimated";

/**
 * Derive a status for the rail: past events are "reported", future ones are
 * "confirmed", and future events flagged as estimated dates are "estimated".
 */
export const eventStatus = (row: EventListRow): EventStatus => {
    const isFuture = row.date ? dayjs.utc(row.date).isAfter(dayjs.utc()) : false;
    if (!isFuture) return "reported";
    return row.estimatedDate ? "estimated" : "confirmed";
};

/** Is this row an earnings event? Matches the rail's transcript / beat-miss UI. */
export const isEarningsRow = (row: EventListRow): boolean => /earning/i.test(row.type || "");

/** Trim the trailing zeros off a decimal string ("0.260000" → "0.26", "4.000000" → "4"). */
const trimDecimalString = (value: string): string => (value.includes(".") ? value.replace(/0+$/, "").replace(/\.$/, "") : value);

/**
 * Compact detail string for dividend / split rows, from the family-specific
 * extras the list serializer sends as hidden columns: "0.26 USD" for a
 * dividend, "4:1" for a split. Null for every other row (the extras are only
 * set on their own family's rows), so callers can render nothing.
 */
export const eventDetail = (row: EventListRow): string | null => {
    if (row.amount != null && row.amount !== "") {
        return `${trimDecimalString(row.amount)}${row.currency ? ` ${row.currency}` : ""}`;
    }
    if (row.ratioFrom != null && row.ratioFrom !== "" && row.ratioTo != null && row.ratioTo !== "") {
        return `${trimDecimalString(row.ratioFrom)}:${trimDecimalString(row.ratioTo)}`;
    }
    return null;
};

/**
 * Match an events-list `type` against a mixed-stats `name`. Both derive from the
 * event's family name, but wording/case can differ slightly between the two
 * endpoints (e.g. upcoming vs reported earnings), so compare normalised and treat
 * any two earnings-family labels as equal.
 */
export const sameEventType = (a: string | null | undefined, b: string | null | undefined): boolean => {
    const na = (a ?? "").trim().toLowerCase();
    const nb = (b ?? "").trim().toLowerCase();
    if (!na || !nb) return false;
    if (na === nb) return true;
    return /earning/.test(na) && /earning/.test(nb);
};

/** Currency-ish compact formatting for revenue / EBIT / net income actuals. */
export const formatBigNumber = (value: number | null | undefined, currencySymbol = "$"): string => {
    if (value == null || Number.isNaN(value)) return "—";
    const abs = Math.abs(value);
    if (abs >= 1e9) return `${currencySymbol}${(value / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `${currencySymbol}${(value / 1e6).toFixed(1)}M`;
    if (abs >= 1e3) return `${currencySymbol}${(value / 1e3).toFixed(1)}K`;
    return `${currencySymbol}${value.toFixed(2)}`;
};

/** EPS-style formatting (small absolute values, 2dp). */
export const formatEps = (value: number | null | undefined, currencySymbol = "$"): string => {
    if (value == null || Number.isNaN(value)) return "—";
    return `${value < 0 ? "-" : ""}${currencySymbol}${Math.abs(value).toFixed(2)}`;
};

export const formatPct = (value: number | null | undefined, withSign = true): string => {
    if (value == null || Number.isNaN(value)) return "—";
    const sign = withSign && value > 0 ? "+" : "";
    return `${sign}${value.toFixed(2)}%`;
};

/** Cards for the event hero: EPS / Revenue / EBIT / Net Income beat-miss tiles. */
export interface HeroMetric {
    label: string;
    actual: string;
    estimate: string;
    surprisePct: number | null;
    result: FinancialMetricBlock["result"];
}

export const buildHeroMetrics = (fin: EarningsFinancials | null | undefined): HeroMetric[] => {
    if (!fin) return [];
    const metrics: HeroMetric[] = [];
    const push = (label: string, block: FinancialMetricBlock | null | undefined, fmt: (v: number | null | undefined) => string) => {
        if (!block) return;
        if (block.actual == null && block.estimate == null) return;
        metrics.push({
            label,
            actual: fmt(block.actual),
            estimate: fmt(block.estimate),
            surprisePct: block.surprisePct ?? null,
            result: block.result ?? null,
        });
    };
    push("Adjusted EPS", fin.eps, (v) => formatEps(v));
    push("Revenue", fin.revenue, (v) => formatBigNumber(v));
    push("Adj. EBIT", fin.ebit, (v) => formatBigNumber(v));
    push("Net Income", fin.netIncome, (v) => formatBigNumber(v));
    return metrics;
};

/** Index the financials response by eventId for fast rail / hero merging. */
export const indexFinancialsByEvent = (rows: EarningsFinancials[] | null | undefined): Record<number, EarningsFinancials> => {
    const map: Record<number, EarningsFinancials> = {};
    for (const row of rows ?? []) {
        if (row && row.eventId != null) map[row.eventId] = row;
    }
    return map;
};

/**
 * ±window (ms) within which a financials period is considered the same quarter as
 * the event. It must cover the longest realistic reporting lag (annual filings can
 * land up to ~90 days after period end) while staying below the distance to the
 * PREVIOUS quarter's period end (one quarter ≈ 91 days plus that quarter's own lag,
 * so ≥ ~115 days in practice). The old ±120 days crossed that line and let an event
 * inherit the previous quarter's financials.
 */
const FINANCIALS_MATCH_WINDOW_MS = 90 * 24 * 60 * 60 * 1000; // ±90 days

/**
 * Find the financials row that best matches a selected event.
 *
 * Financials rows are keyed by the earnings ANNOUNCEMENT event id, but the user
 * often selects the earnings CALL event (a different id, same quarter). So rather
 * than only matching the exact eventId, we pick the row whose reporting period
 * (`periodEndDate`, falling back to `eventAt`) is closest to the event's date,
 * provided it is within a sane window. The exact eventId match is preferred when
 * present. Only earnings-type events get a match; everything else returns null.
 *
 * @param financials The full earnings-financials response.
 * @param eventRow The currently selected event.
 * @returns The matching financials row, or null when there is no sensible match.
 */
export const matchFinancialsForEvent = (financials: EarningsFinancials[] | null | undefined, eventRow: EventListRow | null | undefined): EarningsFinancials | null => {
    if (!eventRow || !isEarningsRow(eventRow)) return null;
    // Financials rows are REPORTED actuals. An upcoming event has none of its own,
    // and the nearest-period fallback below would attach the previous quarter's
    // actuals and beat/miss chips to it — directly under an "Event not yet
    // reported" banner. Show nothing instead.
    if (eventStatus(eventRow) !== "reported") return null;
    const rows = financials ?? [];
    if (rows.length === 0) return null;

    // Prefer an exact eventId match when the financials are keyed by this event.
    const exact = rows.find((r) => r && r.eventId === eventRow.id);
    if (exact) return exact;

    const target = eventRow.date;
    if (target == null) return null;

    const periodOf = (r: EarningsFinancials): number | null => r.periodEndDate ?? r.eventAt ?? null;

    let best: EarningsFinancials | null = null;
    let bestDist = Infinity;
    for (const r of rows) {
        const period = r ? periodOf(r) : null;
        if (period == null) continue;
        const dist = Math.abs(period - target);
        if (dist < bestDist) {
            best = r;
            bestDist = dist;
        }
    }
    return best && bestDist <= FINANCIALS_MATCH_WINDOW_MS ? best : null;
};

// ---- Quarterly statistics table helpers -------------------------------------

export const STAT_WINDOWS: { key: keyof QuarterlyStatRow; label: string }[] = [
    { key: "1WeekAgoChangePct", label: "Week Before" },
    { key: "1DayAfterChangePct", label: "Next Trading Day" },
    { key: "1WeekAfterChangePct", label: "Week After" },
    { key: "1MonthAfterChangePct", label: "Month After" },
];

/** Preferred display order for the quarterly rows ("Earnings Total" first). */
const QUARTER_ORDER = ["Earnings Total", "Q1", "Q2", "Q3", "Q4"];

export interface QuarterlyTableRow {
    name: string;
    occurrences: number;
    cells: (StatWindow | null)[];
}

/** Flatten the quarterly stats dict into ordered rows for the table. */
export const buildQuarterlyRows = (stats: QuarterlyStatistics | null | undefined): QuarterlyTableRow[] => {
    if (!stats) return [];
    const rows: QuarterlyTableRow[] = [];
    const keys = Object.keys(stats).filter((k) => k !== "aggregates" && k !== "obfuscated");
    const ordered = [...keys].sort((a, b) => {
        const ia = QUARTER_ORDER.indexOf(a);
        const ib = QUARTER_ORDER.indexOf(b);
        if (ia === -1 && ib === -1) return a.localeCompare(b);
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
    });
    for (const key of ordered) {
        const row = stats[key] as QuarterlyStatRow;
        if (!row || typeof row !== "object") continue;
        // Render the row if it has totalEvents OR at least one known window dict —
        // partial backend shapes (windows present but totalEvents missing, or
        // some windows null) should still produce a row rather than being dropped.
        const hasAnyWindow = STAT_WINDOWS.some(({ key: wk }) => wk in row);
        if (!("totalEvents" in row) && !hasAnyWindow) continue;
        rows.push({
            name: key,
            occurrences: row.totalEvents ?? 0,
            cells: STAT_WINDOWS.map(({ key: wk }) => (row[wk] as StatWindow | undefined) ?? null),
        });
    }
    return rows;
};

/** Map an AI tone to one of the theme colours passed in by the caller. */
export const toneColor = (tone: string, positive: string, negative: string, neutral: string): string => (tone === "up" ? positive : tone === "down" ? negative : neutral);

/** Build the per-type reaction history (next-day move per occurrence). */
export interface ReactionPoint {
    label: string;
    date: string;
    reaction: number | null;
}

export const buildReactionHistory = (stats: MixedEventStat[] | null | undefined, typeName: string): ReactionPoint[] => {
    const filtered = (stats ?? []).filter((s) => s && sameEventType(s.name, typeName) && s.date != null);
    // CIQ emits the release-date / announcement / call for one quarter as separate
    // events on the SAME DAY, which previously drew 2-3 bars per quarter. Collapse to
    // one bar per day, preferring an occurrence that actually has a next-day value.
    const byDay = new Map<string, MixedEventStat>();
    for (const s of filtered) {
        const day = dayjs.utc(s.date).format("YYYY-MM-DD");
        const existing = byDay.get(day);
        if (!existing || (existing["1DayAfter"] == null && s["1DayAfter"] != null)) {
            byDay.set(day, s);
        }
    }
    return Array.from(byDay.values())
        .sort((a, b) => (a.date ?? 0) - (b.date ?? 0))
        .map((s) => ({
            label: dayjs.utc(s.date).format("MMM 'YY"),
            date: dayjs.utc(s.date).format("MMM YYYY"),
            reaction: s["1DayAfter"] ?? null,
        }));
};
